import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Plus } from 'lucide-react';
import { TYPE } from '../../constants/typeScale';
import Tooltip from '../ui/Tooltip';
import { ExplorerRow, ExplorerSection } from './ExplorerRow';
import { useOptionalToast } from '../../contexts/NotificationContext';
import { useConfirm } from '../../hooks/useConfirm';
import type { ConfirmOptions } from '../../hooks/useConfirm';
import {
  listStoryboards, listSeries, updateSeries, updateStoryboard, deleteStoryboard, createStoryboard,
  SERIES_CHANGED_EVENT, type Storyboard, type Series,
} from '../../services/studioService';

/**
 * ASSET EXPLORER — the creator's own work, as a file tree.
 *
 * This replaces the chat-contact list that used to fill the left pane. A chat list made sense when the product
 * was a group chat; it does not when the product is a studio, where the thing you navigate is your WORK:
 * series and their episodes, and standalone videos.
 *
 * Shaped like a file explorer on purpose — collapsible sections with children is the one navigation idiom
 * nobody has to be taught. Selecting a production NAVIGATES (`/dashboard/{slug}`) rather than holding its own
 * open/closed state, because productions already have addresses; the explorer is a view over the URL space,
 * not a second source of truth.
 *
 * Only what EXISTS is listed. Reference Images and the Video Library are specced (RF3-LIBRARY) but unbuilt, so
 * they are absent rather than shown empty — we never advertise a section that cannot be opened.
 *
 * RF3-EXPLORER D — this is also where a title is RENAMED, at both levels (series and episode). The Setup stage
 * names a production at birth; the explorer is the one place it is renamed afterwards, so the same fact is
 * never editable from two competing surfaces.
 *
 * RF3-EXPLORER D2 — and where a production is DELETED. Only a production: a SERIES is not deletable from here,
 * because `deleteSeries` removes the series document alone and leaves its episodes pointing at a series that no
 * longer exists — and RF3-SCHEMA says every production belongs to one, so an orphaned episode is not a state we
 * are allowed to create. Every series listed here has at least one episode by construction (the groups are
 * built FROM the episodes), so the honest answer is to offer no series delete at all rather than a control that
 * would always have to refuse. Emptying a series of its episodes removes it from this list; whether the empty
 * series document should then go too is a backend decision (logged in BUGS.md), not one to guess at here.
 */

type Group = { key: string; label: string; episodes: Storyboard[]; renameable: boolean };

/**
 * Rename, optimistically. The new title paints immediately; if the write fails the PREVIOUS list is put back
 * and the creator is told — a rename that silently keeps a name the server never accepted is the one outcome
 * we cannot ship. Shared by both levels so a series and an episode behave identically.
 */
async function commitRename<T extends { id: string; title: string }>(
  id: string,
  title: string,
  list: T[],
  setList: (next: T[]) => void,
  save: (id: string, patch: { title: string }) => Promise<unknown>,
  onError: () => void,
): Promise<void> {
  setList(list.map((x) => (x.id === id ? { ...x, title } : x)));
  try { await save(id, { title }); } catch { setList(list); onError(); }
}

/**
 * Delete, optimistically — the mirror of `commitRename`: the row goes at once, and the PREVIOUS list is put
 * back if the server refuses, so a delete that never happened can never look like it did.
 */
async function commitDelete(
  b: Storyboard,
  list: Storyboard[],
  setList: (next: Storyboard[]) => void,
  onDeleted: () => void,
  onError: () => void,
): Promise<void> {
  setList(list.filter((x) => x.id !== b.id));
  try { await deleteStoryboard(b.id); onDeleted(); } catch { setList(list); onError(); }
}

/** The pane's non-list states — a shelf that is loading, or one that has nothing on it yet. */
const ExplorerNote: React.FC<{ className: string; children: React.ReactNode }> = ({ className, children }) => (
  <p className={`px-3 py-4 text-xs leading-relaxed text-secondary-400 ${className}`}>{children}</p>
);

/** Group the creator's work the way they think of it: each series with its episodes, then the loose videos. */
function groupWork(boards: Storyboard[] | null, series: Series[]): { groups: Group[]; singles: Storyboard[] } {
  const titleFor = (id: string) => series.find((s) => s.id === id)?.title || 'Untitled series';
  const byId = new Map<string, Storyboard[]>();
  const loose: Storyboard[] = [];
  for (const b of boards || []) {
    if (b.series_id) byId.set(b.series_id, [...(byId.get(b.series_id) || []), b]);
    else loose.push(b);
  }
  return {
    groups: [...byId.entries()].map(([key, eps]) => ({
      key,
      label: titleFor(key),
      // Only a series we actually LOADED can be renamed: a group whose series document never arrived would
      // accept the edit and then bounce it off the server, which is worse than not offering the pencil.
      renameable: series.some((s) => s.id === key),
      episodes: eps.sort((a, z) => (a.episode_number || 0) - (z.episode_number || 0)),
    })),
    singles: loose,
  };
}

/**
 * NAME WHAT IS DESTROYED. A generic "are you sure?" is fine for a reversible action; this one takes generated
 * media the creator PAID credits for, and no undo exists, so the dialog spells out the title, where it sits,
 * how many shots go with it and whether a finished film is among them. A creator who has three near-identical
 * drafts open must be able to tell from this sentence alone whether it is the right one.
 */
function deletePrompt(b: Storyboard, seriesTitle?: string): ConfirmOptions {
  const title = b.title || 'this video';
  const where = seriesTitle
    ? ` — episode ${b.episode_number || 1} of ${seriesTitle}`
    : '';
  const shots = b.shots?.length || 0;
  const made = [
    shots ? `${shots} shot${shots === 1 ? '' : 's'}` : '',
    b.final_video_path ? 'the film already rendered from them' : '',
  ].filter(Boolean).join(' and ');
  const loss = made
    ? `Deleting it also deletes ${made}. Generated media cannot be recovered, and the credits spent making it are not refunded.`
    : 'Nothing has been generated for it yet, so no credits are lost — but the draft itself cannot be recovered.';
  return {
    title: `Delete ${title}?`,
    description: `${title}${where} will be deleted permanently. ${loss}`,
    confirmText: 'Delete it',
    cancelText: 'Keep it',
    variant: 'destructive',
  };
}

/**
 * WHICH LEVEL THE CREATOR IS ON, not just which production (BUGS.md 2026-08-30).
 *
 * The Studio is opened by an EPISODE address even at project level — `/dashboard/{episode-slug}/project` — so
 * the slug alone cannot tell "in this episode" from "in this episode's project". Without the stage the explorer
 * highlighted the episode row while the creator was editing the project, which is exactly the confusion the
 * two-level split exists to prevent.
 */
const AssetExplorer: React.FC<{
  activeSlug?: string | null;
  /** The URL's stage segment. 'project' means the PROJECT row is where the creator is, not the episode. */
  activeStage?: string | null;
  className?: string;
}> = ({ activeSlug, activeStage, className = '' }) => {
  /** True when the creator is on the project surface, reached through one of its episodes. */
  const onProject = activeStage === 'project';
  const [boards, setBoards] = useState<Storyboard[] | null>(null);
  const [series, setSeries] = useState<Series[]>([]);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  /** Which row is in edit mode, as `series:{id}` / `board:{id}`. One at a time — this is a list, not a form. */
  const [editing, setEditing] = useState<string | null>(null);
  const { showError } = useOptionalToast();
  const [confirm, confirmDialog] = useConfirm();
  const navigate = useNavigate();

  /**
   * The shelf, and a way to refresh it. Named so the listener below can reuse it rather than duplicating the
   * fetch — a second copy is how the two drift apart again.
   */
  const load = useCallback(() => {
    let alive = true;
    Promise.all([listStoryboards(), listSeries().catch(() => [])])
      .then(([b, s]) => { if (alive) { setBoards(b); setSeries(s); } })
      .catch(() => { if (alive) setBoards([]); });   // signed out or offline: an empty shelf, never an error
    return () => { alive = false; };
  }, []);

  useEffect(() => load(), [load]);

  /**
   * A RENAME MADE ELSEWHERE LANDS HERE (BUGS.md 2026-08-30). This list used to be fetched once and never again,
   * so renaming a project on the Project page left this pane showing the title it saw at startup. The service
   * announces every series write centrally; this refetches rather than patching one field, so a create or a
   * delete from anywhere is covered by the same listener.
   */
  useEffect(() => {
    const onChanged = () => { load(); };
    window.addEventListener(SERIES_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(SERIES_CHANGED_EVENT, onChanged);
  }, [load]);

  /** Series with their episodes, plus everything that belongs to no series. */
  const { groups, singles } = useMemo(() => groupWork(boards, series), [boards, series]);

  const go = useCallback((b: Storyboard) => navigate(`/dashboard/${b.slug || b.id}`), [navigate]);

  /**
   * SELECTING THE PROJECT OPENS THE PROJECT (owner 2026-08-24: *"when I highlight 'project' on Project
   * Explorer, that's where I can set the Project info, not per episode, because the Project info is inherited
   * across to the episodes"*).
   *
   * The Studio is opened by an EPISODE address, so a project is reached through one of its own episodes — but
   * what the page then edits is the SERIES document, so the facts are genuinely project-level whichever
   * episode carried you there. Any episode does; the first is the least surprising.
   */
  const goProject = useCallback((g: Group) => {
    /**
     * STAY ON THE EPISODE YOU CAME FROM (owner 2026-09-13: *"when I am in an episode and click project, I
     * can't go back"*).
     *
     * A project is reached THROUGH one of its episodes, and it used to always be the first — so opening the
     * project from episode 5 quietly re-addressed the creator as episode 1, and going back took them to the
     * wrong one. The page shows the same series either way, so there is no reason to move them: keep the
     * episode they are standing on when it belongs to this series, and fall back to the first only when they
     * are arriving from somewhere else entirely.
     */
    const here = activeSlug ? g.episodes.find((e) => e.slug === activeSlug || e.id === activeSlug) : null;
    const through = here || g.episodes[0];
    if (!through) return;
    navigate(`/dashboard/${through.slug || through.id}/project`);
  }, [navigate, activeSlug]);
  const toggle = (k: string) => setOpen((p) => ({ ...p, [k]: !p[k] }));

  const failed = useCallback((what: string) => () =>
    showError('Rename failed', `That ${what} title could not be saved. Please try again.`), [showError]);

  const renameSeries = useCallback((id: string, title: string) => {
    setEditing(null);
    void commitRename(id, title, series, (v) => setSeries(v), updateSeries, failed('series'));
  }, [series, failed]);

  const renameBoard = useCallback((id: string, title: string) => {
    setEditing(null);
    void commitRename(id, title, boards || [], (v) => setBoards(v), updateStoryboard, failed('video'));
  }, [boards, failed]);

  /**
   * Delete a production, optimistically — the row goes the moment the creator confirms, and comes back if the
   * server refuses, exactly like the rename above. If they just deleted the production they were editing, the
   * Studio is sent back to the launchpad: leaving it open on a document that no longer exists would let the
   * next autosave resurrect a deleted video.
   */
  const removeBoard = useCallback(async (b: Storyboard, seriesTitle?: string) => {
    if (!(await confirm(deletePrompt(b, seriesTitle)))) return;
    await commitDelete(b, boards || [], setBoards,
      () => { if (activeSlug && (b.slug === activeSlug || b.id === activeSlug)) navigate('/dashboard'); },
      () => showError('Delete failed', `${b.title || 'That video'} could not be deleted. Please try again.`));
  }, [boards, confirm, activeSlug, navigate, showError]);

  /**
   * START SOMETHING NEW, FROM THE EXPLORER (owner 2026-08-24: *"give a way for the user to add a new episode
   * or new project"*). Both are the same call — a storyboard is created either way. What differs is whether it
   * carries a project: WITH one it becomes the next episode of that project; WITHOUT one the server mints a
   * project for it (RF3-SCHEMA: every production belongs to a series, so a "new project" is a first episode).
   *
   * A new project opens on its PROJECT page, where the plot and world are stated; a new episode opens on
   * Story, because its project has already said all of that.
   */
  const [creating, setCreating] = useState(false);
  const create = useCallback(async (group?: Group) => {
    if (creating) return;
    setCreating(true);
    try {
      const next = group ? Math.max(0, ...group.episodes.map((e) => e.episode_number || 0)) + 1 : 1;
      const made = await createStoryboard(group ? { series_id: group.key, episode_number: next } : {});
      setBoards((prev) => [made, ...(prev || [])]);
      if (!group) {
        // A freshly minted project is not in `series` yet, so pull the list again — otherwise its group header
        // would read "Untitled series" and offer no rename until the next reload.
        listSeries().then(setSeries).catch(() => { /* the group still lists; only its name waits */ });
      }
      navigate(`/dashboard/${made.slug || made.id}/${group ? 'story' : 'project'}`);
    } catch {
      showError('Could not start that', group ? 'The new episode was not created. Please try again.' : 'The new series was not created. Please try again.');
    } finally {
      setCreating(false);
    }
  }, [creating, navigate, showError]);

  /** The props every renameable row needs, so an episode row and a series header behave identically. */
  const renameProps = (key: string, commit: (title: string) => void) => ({
    renaming: editing === key,
    onRenameStart: () => setEditing(key),
    onRenameCancel: () => setEditing(null),
    onRename: commit,
  });

  /** The pane's own header: the one place a creator starts a whole new production. */
  const header = (
    <div className="mb-1 flex items-center gap-1 px-2">
      <span className={`min-w-0 flex-1 ${TYPE.label} text-secondary-400`}>Your series</span>
      <Tooltip content="New series">
        <button
          type="button"
          onClick={() => void create()}
          disabled={creating}
          data-testid="new-project"
          aria-label="New series"
          className="shrink-0 rounded p-1 text-secondary-400 transition hover:text-secondary-800
                     disabled:opacity-40 dark:hover:text-white"
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
      </Tooltip>
    </div>
  );

  if (boards === null) {
    return <ExplorerNote className={`flex items-center gap-2 ${className}`}>
      <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading your work…
    </ExplorerNote>;
  }

  if (!groups.length && !singles.length) {
    return (
      <nav className={`px-1 py-2 ${className}`} aria-label="Your work">
        {header}
        <ExplorerNote className="">Nothing here yet. Start a series and its episodes will appear under it.</ExplorerNote>
      </nav>
    );
  }

  const episodeRow = (seriesTitle: string) => (e: Storyboard) => (
    <ExplorerRow
      key={e.id}
      depth={1}
      label={e.title || 'Untitled episode'}
      sub={e.episode_number ? `E${e.episode_number}` : undefined}
      // A level above means this episode is the ROUTE but not the place — highlighting it here is what made
      // the creator think they were editing the episode when they were editing the show.
      active={!onProject && !!activeSlug && (e.slug === activeSlug || e.id === activeSlug)}
      onClick={() => go(e)}
      onDelete={() => void removeBoard(e, seriesTitle)}
      {...renameProps(`board:${e.id}`, (t) => renameBoard(e.id, t))}
    />
  );

  return (
    <nav className={`px-1 py-2 ${className}`} aria-label="Your work">
      {confirmDialog}
      {header}
      {groups.map((g) => (
        <ExplorerSection
          key={g.key}
          label={g.label}
          count={g.episodes.length}
          isOpen={open[g.key] !== false}   // sections start expanded — the work is the point of the pane
          onToggle={() => toggle(g.key)}
          onSelect={() => goProject(g)}
          selected={g.episodes.some((e) => !!activeSlug && (e.slug === activeSlug || e.id === activeSlug))}
          // On the project stage the SERIES is what is open, so the group row takes the current-row treatment
          // that the episode row has just given up.
          active={onProject && g.episodes.some((e) => !!activeSlug && (e.slug === activeSlug || e.id === activeSlug))}
          onAdd={() => void create(g)}
          addLabel="New episode"
          {...(g.renameable ? renameProps(`series:${g.key}`, (t) => renameSeries(g.key, t)) : {})}
        >
          {g.episodes.map(episodeRow(g.label))}
        </ExplorerSection>
      ))}

      {singles.length > 0 && (
        <ExplorerSection
          label="Single videos"
          count={singles.length}
          isOpen={open.__singles !== false}
          onToggle={() => toggle('__singles')}
        >
          {singles.map((b) => (
            <ExplorerRow
              key={b.id}
              depth={1}
              label={b.title || 'Untitled episode'}
              active={!!activeSlug && (b.slug === activeSlug || b.id === activeSlug)}
              onClick={() => go(b)}
              onDelete={() => void removeBoard(b)}
              {...renameProps(`board:${b.id}`, (t) => renameBoard(b.id, t))}
            />
          ))}
        </ExplorerSection>
      )}
    </nav>
  );
};

export default AssetExplorer;
